import { APIError } from '../../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../../useAPIProvider';

export const useStockLevels = (mspId: string, customerId: string) => {
  return useAPIQuery(['getStockLevels', mspId, customerId], async client => {
    const response = await client.stockLevels.getStockLevels({ params: { mspId, customerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);

    return response.body.items;
  });
};

export const useStockLevel = (mspId: string, customerId: string, id: string) => {
  return useAPIQuery(['getStockLevel', mspId, customerId, id], async client => {
    const response = await client.stockLevels.getStockLevel({ params: { mspId, customerId, id } });
    if (response.status !== 200) throw APIError.fromResponse(response);

    return response.body;
  });
};

interface CreateStockLevelArgs {
  mspId: string;
  customerId: string;
  employeeCount: number;
  churnRate: number;
  growthRate: number;
  stockLevelState: number;
}

export const useCreateStockLevel = () => {
  return useAPIMutation((client, query) => {
    return async ({
      employeeCount,
      churnRate,
      growthRate,
      stockLevelState,
      mspId,
      customerId
    }: CreateStockLevelArgs) => {
      const response = await client.stockLevels.createStockLevel({
        body: { employeeCount, churnRate, growthRate, stockLevelState },
        params: {
          mspId,
          customerId
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getStockLevels']);
      return response.body;
    };
  });
};

interface UpdateStockLevelArgs extends CreateStockLevelArgs {
  id: string;
}

export const useUpdateStockLevel = () => {
  return useAPIMutation((client, query) => {
    return async ({
      id,
      employeeCount,
      churnRate,
      growthRate,
      stockLevelState,
      mspId,
      customerId
    }: UpdateStockLevelArgs) => {
      const response = await client.stockLevels.updateStockLevel({
        params: { mspId, customerId, id },
        body: { employeeCount, churnRate, growthRate, stockLevelState }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getStockLevels']);
      await query.invalidateQueries(['getStockLevel', mspId, customerId, id]);
      return response.body;
    };
  });
};

interface DeleteStockLevelArgs {
  mspId: string;
  customerId: string;
  id: string;
}

export const useDeleteStockLevel = () => {
  return useAPIMutation(client => {
    return async ({ mspId, customerId, id }: DeleteStockLevelArgs) => {
      return await client.stockLevels.deleteStockLevel({ params: { mspId, customerId, id } });
    };
  });
};

interface UpdateStockLevelStatusArgs {
  mspId: string;
  customerId: string;
  id: string;
  state: number;
}

export const useUpdateStockLevelStatus = () => {
  return useAPIMutation((client, query) => {
    return async ({ id, mspId, customerId, state }: UpdateStockLevelStatusArgs) => {
      const response = await client.stockLevels.updateStockLevelStatus({
        params: { mspId, customerId, id },
        body: { state }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getStockLevels']);
      await query.invalidateQueries(['getStockLevel', mspId, customerId, id]);
      return response.body;
    };
  });
};
